<template>
  <FlashNotifications ref="flash" />

  <!-- ── Add / Edit Modal ── -->
  <Transition name="modal">
    <div v-if="showFormModal"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      @click.self="closeForm">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeForm"></div>

      <div class="relative w-full sm:max-w-lg bg-white dark:bg-[#1C2128] rounded-t-2xl sm:rounded-2xl shadow-2xl border-0 sm:border border-gray-100 dark:border-gray-700 modal-card">

        <!-- Modal header -->
        <div class="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700">
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">
            {{ form.id ? 'Edit Webhook' : 'New Webhook' }}
          </h3>
          <button @click="closeForm"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Modal body -->
        <div class="px-6 py-5 space-y-5">

          <!-- Name -->
          <div class="space-y-1.5">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Webhook Name <span class="text-red-400">*</span>
              </label>
              <span class="text-[11px] text-gray-400 tabular-nums">{{ form.name.length }}/30</span>
            </div>
            <input v-model="form.name" type="text" maxlength="30" placeholder="e.g. Booking notifications"
              class="w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
              :class="formErrors.name ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"/>
            <p v-if="formErrors.name" class="text-xs text-red-500 dark:text-red-400">{{ formErrors.name }}</p>
          </div>

          <!-- URL -->
          <div class="space-y-1.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Endpoint URL <span class="text-red-400">*</span>
            </label>
            <div class="relative">
              <input v-model="form.url" type="url" placeholder="https://your-server.com/webhook"
                class="w-full px-3 py-2 pr-9 text-sm border rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow font-mono"
                :class="formErrors.url ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"/>
              <svg v-if="formErrors.url" class="absolute right-2.5 top-2.5 w-4 h-4 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
              <svg v-else-if="urlValid" class="absolute right-2.5 top-2.5 w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
            </div>
            <p v-if="formErrors.url" class="text-xs text-red-500 dark:text-red-400">{{ formErrors.url }}</p>
            <p v-else class="text-[11px] text-gray-400 dark:text-gray-500">Must begin with https:// or http://</p>
          </div>

          <!-- Payload scope -->
          <div class="space-y-2">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Trigger on
            </label>
            <div class="grid grid-cols-2 gap-2">

              <label class="flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-colors"
                :class="form.event_included
                  ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
                <input type="checkbox" v-model="form.event_included" class="sr-only"/>
                <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  :class="form.event_included ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-800'">
                  <svg class="w-4 h-4 transition-colors"
                    :class="form.event_included ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                    fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold"
                    :class="form.event_included ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'">
                    Events
                  </p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Create, update, delete</p>
                </div>
                <div class="ml-auto flex-shrink-0">
                  <div v-if="form.event_included" class="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                    <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <div v-else class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                </div>
              </label>

              <label class="flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-colors"
                :class="form.booking_included
                  ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
                <input type="checkbox" v-model="form.booking_included" class="sr-only"/>
                <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  :class="form.booking_included ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-800'">
                  <svg class="w-4 h-4 transition-colors"
                    :class="form.booking_included ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                    fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"/>
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-semibold"
                    :class="form.booking_included ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'">
                    Bookings
                  </p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Create, cancel</p>
                </div>
                <div class="ml-auto flex-shrink-0">
                  <div v-if="form.booking_included" class="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center">
                    <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <div v-else class="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Modal footer -->
        <div class="flex gap-3 px-6 pb-6 pt-2">
          <button @click="closeForm"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button @click="submitForm" :disabled="saving"
            class="flex-1 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ saving ? 'Saving...' : (form.id ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- ── Delete Confirmation Modal ── -->
  <Transition name="modal">
    <div v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="showDeleteModal = false">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 modal-card">
        <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Delete webhook?</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
          "<strong class="text-gray-700 dark:text-gray-300">{{ deleteTarget?.name }}</strong>" will be permanently removed and will stop sending events.
        </p>
        <div class="flex gap-3">
          <button @click="showDeleteModal = false"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button @click="doDelete" :disabled="deleting"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="deleting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- ── Page ── -->
  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══ MOBILE ══ -->
    <div class="sm:hidden">
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center justify-between px-4 h-14">
          <div class="flex items-center gap-2">
            <RouterLink to="/integrations" data-turbo-prefetch="false"
              class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors -ml-1">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
            </RouterLink>
            <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Webhooks</h1>
            <span v-if="webhooks.length > 0"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
              {{ webhooks.length }}
            </span>
          </div>
          <button @click="openCreate"
            class="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-600/25 active:scale-95 transition-transform">
            <svg class="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile list -->
      <div class="px-4 pt-4 pb-28 space-y-3">

        <!-- Empty state -->
        <div v-if="webhooks.length === 0"
          class="flex flex-col items-center justify-center pt-24 pb-16 text-center">
          <div class="w-20 h-20 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-5">
            <WebhookIcon class="w-10 h-10 opacity-40" />
          </div>
          <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No webhooks yet</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 max-w-[240px] mb-6">
            Add a webhook to receive real-time HTTP notifications for events and bookings.
          </p>
          <button @click="openCreate"
            class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-2xl text-sm shadow-md shadow-blue-600/30">
            Create webhook
          </button>
        </div>

        <!-- Webhook cards (mobile) -->
        <div v-for="webhook in webhooks" :key="webhook.id"
          class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">

          <!-- Top row -->
          <div class="flex items-start gap-3 px-4 pt-4 pb-3">
            <div class="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
              <WebhookIcon class="w-6 h-6 opacity-70" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between gap-2">
                <p class="font-semibold text-sm text-gray-900 dark:text-white leading-tight">{{ webhook.name }}</p>
                <button type="button" @click="toggleActive(webhook)"
                  :class="webhook.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
                  <span :class="webhook.active ? 'translate-x-5' : 'translate-x-0.5'"
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200"/>
                </button>
              </div>
              <p class="text-xs text-gray-400 dark:text-gray-500 font-mono truncate mt-0.5">{{ webhook.url }}</p>
              <div class="flex items-center gap-1.5 mt-2 flex-wrap">
                <span v-if="webhook.event_included"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400">
                  Events
                </span>
                <span v-if="webhook.booking_included"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                  Bookings
                </span>
                <span v-if="!webhook.event_included && !webhook.booking_included"
                  class="text-[10px] text-gray-400 dark:text-gray-600 italic">No triggers selected</span>
                <span class="text-gray-200 dark:text-gray-700 text-xs">·</span>
                <span class="text-[10px] text-gray-400 dark:text-gray-500">{{ webhook.updated_at_ago }}</span>
              </div>
            </div>
          </div>

          <!-- Action row -->
          <div class="flex items-center gap-1 px-4 pb-3 border-t border-gray-50 dark:border-gray-800 pt-2.5">
            <button @click="testWebhook(webhook)" :disabled="testingId === webhook.id"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60">
              <svg v-if="testingId === webhook.id" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"/>
              </svg>
              {{ testingId === webhook.id ? 'Sending...' : 'Test' }}
            </button>
            <div class="flex-1"></div>
            <button @click="openEdit(webhook)"
              class="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
              </svg>
            </button>
            <button @click="openDelete(webhook)"
              class="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- ══ END MOBILE ══ -->


    <!-- ══ DESKTOP ══ -->
    <div class="hidden sm:flex sm:flex-col sm:h-app-screen">

      <!-- Desktop header -->
      <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between px-6 py-3.5">
          <div class="flex items-center gap-3">
            <RouterLink to="/integrations" data-turbo-prefetch="false"
              class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              Integrations
            </RouterLink>
            <div class="h-4 w-px bg-gray-200 dark:bg-gray-700"></div>
            <div>
              <div class="flex items-center gap-2.5">
                <h1 class="text-xl font-bold text-gray-900 dark:text-white">Webhooks</h1>
                <span v-if="webhooks.length > 0"
                  class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
                  {{ webhooks.length }}
                </span>
              </div>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Receive real-time HTTP notifications for events and bookings.
              </p>
            </div>
          </div>
          <button @click="openCreate"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            New Webhook
          </button>
        </div>
      </div>

      <!-- Desktop content -->
      <div class="flex-1 overflow-y-auto px-6 py-6">
        <div class="max-w-4xl mx-auto">

          <!-- Empty state -->
          <div v-if="webhooks.length === 0"
            class="flex flex-col items-center justify-center py-32 text-center">
            <div class="w-24 h-24 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
              <WebhookIcon class="w-12 h-12 opacity-40" />
            </div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No webhooks yet</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-8">
              Add a webhook endpoint to receive real-time HTTP POST notifications whenever events or bookings are created, updated, or cancelled.
            </p>
            <button @click="openCreate"
              class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-sm text-sm">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Create your first webhook
            </button>
          </div>

          <!-- Webhook list -->
          <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/80 overflow-hidden">

            <!-- Table header -->
            <div class="grid grid-cols-[2.5rem_1fr_auto_auto_auto_auto] items-center gap-4 px-6 py-3 bg-gray-50 dark:bg-gray-800/40 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <span></span>
              <span>Webhook</span>
              <span class="hidden lg:block w-32 text-center">Triggers</span>
              <span class="hidden lg:block w-36 text-right">Last updated</span>
              <span class="w-16 text-center">Active</span>
              <span class="w-28 text-right">Actions</span>
            </div>

            <!-- Webhook rows -->
            <div v-for="webhook in webhooks" :key="webhook.id"
              class="grid grid-cols-[2.5rem_1fr_auto_auto_auto_auto] items-center gap-4 px-6 py-4 hover:bg-gray-50/60 dark:hover:bg-gray-800/30 transition-colors">

              <!-- Icon -->
              <div class="w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <WebhookIcon class="w-6 h-6 opacity-70" />
              </div>

              <!-- Name + URL -->
              <div class="min-w-0">
                <p class="font-semibold text-sm text-gray-900 dark:text-white leading-tight">{{ webhook.name }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 font-mono truncate mt-0.5">{{ webhook.url }}</p>
              </div>

              <!-- Trigger badges -->
              <div class="hidden lg:flex w-32 items-center justify-center gap-1">
                <span v-if="webhook.event_included"
                  class="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400">
                  Events
                </span>
                <span v-if="webhook.booking_included"
                  class="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                  Bookings
                </span>
                <span v-if="!webhook.event_included && !webhook.booking_included"
                  class="text-gray-300 dark:text-gray-600 text-sm">—</span>
              </div>

              <!-- Timestamp -->
              <div class="hidden lg:block w-36 text-right">
                <p class="text-xs text-gray-400 dark:text-gray-500">{{ webhook.updated_at_ago }}</p>
              </div>

              <!-- Toggle -->
              <div class="w-16 flex justify-center">
                <button type="button" @click="toggleActive(webhook)"
                  :class="webhook.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
                  <span :class="webhook.active ? 'translate-x-5' : 'translate-x-0.5'"
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200"/>
                </button>
              </div>

              <!-- Actions -->
              <div class="w-28 flex items-center justify-end gap-1">
                <!-- Test -->
                <button @click="testWebhook(webhook)" :disabled="testingId === webhook.id"
                  :title="testingId === webhook.id ? 'Sending test...' : 'Send test payload'"
                  class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50">
                  <svg v-if="testingId === webhook.id" class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  <svg v-else class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"/>
                  </svg>
                  {{ testingId === webhook.id ? '...' : 'Test' }}
                </button>

                <!-- Edit -->
                <button @click="openEdit(webhook)" title="Edit"
                  class="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                  </svg>
                </button>

                <!-- Delete -->
                <button @click="openDelete(webhook)" title="Delete"
                  class="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Info panel -->
          <div v-if="webhooks.length > 0"
            class="mt-4 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/20 px-5 py-4 flex gap-3.5">
            <svg class="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
            </svg>
            <div>
              <p class="text-sm font-semibold text-blue-800 dark:text-blue-300">Webhook payloads are sent as HTTP POST</p>
              <p class="text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5 leading-relaxed">
                Use the Test button to send a sample payload to your endpoint. Your server must return a 2xx status code within 10 seconds.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
    <!-- ══ END DESKTOP ══ -->

  </div>
</template>

<script setup>
import { ref, reactive, computed, defineComponent, h } from 'vue'
import { RouterLink } from 'vue-router'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

// ── Inline webhook icon (brand SVG, scaled) ──
const WebhookIcon = defineComponent({
  render() {
    return h('svg', { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 256 239', class: this.$attrs.class ?? '' }, [
      h('path', { d: 'M119.54 100.503c-10.61 17.836-20.775 35.108-31.152 52.25-2.665 4.401-3.984 7.986-1.855 13.58 5.878 15.454-2.414 30.493-17.998 34.575-14.697 3.851-29.016-5.808-31.932-21.543-2.584-13.927 8.224-27.58 23.58-29.757 1.286-.184 2.6-.205 4.762-.367l23.358-39.168C73.612 95.465 64.868 78.39 66.803 57.23c1.368-14.957 7.25-27.883 18-38.477 20.59-20.288 52.002-23.573 76.246-8.001 23.284 14.958 33.948 44.094 24.858 69.031-6.854-1.858-13.756-3.732-21.343-5.79 2.854-13.865.743-26.315-8.608-36.981-6.178-7.042-14.106-10.733-23.12-12.093-18.072-2.73-35.815 8.88-41.08 26.618-5.976 20.13 3.069 36.575 27.784 48.967z', fill: '#C73A63' }),
      h('path', { d: 'M149.841 79.41c7.475 13.187 15.065 26.573 22.587 39.836 38.02-11.763 66.686 9.284 76.97 31.817 12.422 27.219 3.93 59.457-20.465 76.25-25.04 17.238-56.707 14.293-78.892-7.851 5.654-4.733 11.336-9.487 17.407-14.566 21.912 14.192 41.077 13.524 55.305-3.282 12.133-14.337 11.87-35.714-.615-49.75-14.408-16.197-33.707-16.691-57.035-1.143-9.677-17.168-19.522-34.199-28.893-51.491-3.16-5.828-6.648-9.21-13.77-10.443-11.893-2.062-19.571-12.275-20.032-23.717-.453-11.316 6.214-21.545 16.634-25.53 10.322-3.949 22.435-.762 29.378 8.014 5.674 7.17 7.477 15.24 4.491 24.083-.83 2.466-1.905 4.852-3.07 7.774z', fill: '#4B4B4B' }),
      h('path', { d: 'M167.707 187.21h-45.77c-4.387 18.044-13.863 32.612-30.19 41.876-12.693 7.2-26.373 9.641-40.933 7.29-26.808-4.323-48.728-28.456-50.658-55.63-2.184-30.784 18.975-58.147 47.178-64.293 1.947 7.071 3.915 14.21 5.862 21.264-25.876 13.202-34.832 29.836-27.59 50.636 6.375 18.304 24.484 28.337 44.147 24.457 20.08-3.962 30.204-20.65 28.968-47.432 19.036 0 38.088-.197 57.126.097 7.434.117 13.173-.654 18.773-7.208 9.22-10.784 26.191-9.811 36.121.374 10.148 10.409 9.662 27.157-1.077 37.127-10.361 9.62-26.73 9.106-36.424-1.26-1.992-2.136-3.562-4.673-5.533-7.298z', fill: '#4A4A4A' }),
    ])
  },
})

// ── Props ──
const props = defineProps(['webhooks_json'])

// ── Composables ──
const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { I18N } = useConstants()

// ── State ──
const webhooks = ref(JSON.parse(props.webhooks_json))

const showFormModal   = ref(false)
const showDeleteModal = ref(false)
const saving          = ref(false)
const deleting        = ref(false)
const testingId       = ref(null)
const deleteTarget    = ref(null)

const form = reactive({
  id: '',
  name: '',
  url: '',
  event_included: false,
  booking_included: false,
})

const formErrors = ref({})

// ── Computed ──
const urlValid = computed(() =>
  form.url.trim().startsWith('http://') || form.url.trim().startsWith('https://')
)

// ── Form helpers ──
function openCreate() {
  Object.assign(form, { id: '', name: '', url: '', event_included: false, booking_included: false })
  formErrors.value = {}
  showFormModal.value = true
}

function openEdit(webhook) {
  Object.assign(form, {
    id: webhook.id,
    name: webhook.name,
    url: webhook.url,
    event_included: webhook.event_included,
    booking_included: webhook.booking_included,
  })
  formErrors.value = {}
  showFormModal.value = true
}

function closeForm() {
  showFormModal.value = false
  formErrors.value = {}
}

function validateForm() {
  const errs = {}
  if (!form.name.trim())  errs.name = 'Name is required'
  if (!form.url.trim())   errs.url  = 'URL is required'
  else if (!urlValid.value) errs.url = 'URL must start with https:// or http://'
  formErrors.value = errs
  return Object.keys(errs).length === 0
}

async function submitForm() {
  if (!validateForm()) return
  saving.value = true
  form.id ? await updateWebhook() : await createWebhook()
  saving.value = false
}

async function createWebhook() {
  const res = await axiosRequest('POST', '/api/v1/integrations/webhooks', { webhook: form }, false)
  if (!res.js_error && res.response.success) {
    webhooks.value.unshift(res.response.data.webhook)
    closeForm()
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show(res.error?.data?.message || 'Failed to create webhook.', 'alert')
  }
}

async function updateWebhook() {
  const res = await axiosRequest('PUT', `/api/v1/integrations/webhooks/${form.id}`, { webhook: form }, false)
  if (!res.js_error && res.response.success) {
    const idx = webhooks.value.findIndex((w) => w.id === form.id)
    if (idx !== -1) webhooks.value[idx] = res.response.data.webhook
    closeForm()
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show(res.error?.data?.message || 'Failed to update webhook.', 'alert')
  }
}

// ── Delete ──
function openDelete(webhook) {
  deleteTarget.value = webhook
  showDeleteModal.value = true
}

async function doDelete() {
  if (!deleteTarget.value) return
  deleting.value = true
  const res = await axiosRequest('DELETE', `/api/v1/integrations/webhooks/${deleteTarget.value.id}`, {}, true)
  deleting.value = false
  showDeleteModal.value = false
  if (!res.js_error && res.response.success) {
    webhooks.value = webhooks.value.filter((w) => w.id !== deleteTarget.value.id)
    deleteTarget.value = null
  }
}

// ── Toggle active ──
async function toggleActive(webhook) {
  const newActive = !webhook.active
  webhook.active = newActive
  const res = await axiosRequest(
    'PUT',
    `/api/v1/integrations/webhooks/${webhook.id}`,
    { webhook: { active: newActive } },
    false,
  )
  if (res.js_error || !res.response.success) {
    webhook.active = !newActive
  }
}

// ── Test ──
async function testWebhook(webhook) {
  testingId.value = webhook.id
  const res = await axiosRequest('POST', '/api/v1/integrations/webhooks/test_webhook', { url: webhook.url }, false)
  testingId.value = null
  if (!res.js_error && res.response.success) {
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show('Test failed. Check your endpoint is reachable.', 'alert')
  }
}
</script>

<style scoped>
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from,
.modal-leave-to   { opacity: 0; }
.modal-enter-active .modal-card { transition: transform 0.2s ease; }
.modal-enter-from  .modal-card  { transform: scale(0.96) translateY(8px); }
</style>
